import React from "react";
import { useTranslation } from "react-i18next";
import { FormikProps } from "formik";
import { useFeatureEntitlements } from "@frontegg/react";
import Role from "enums/Role";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import { TransactionJournal } from "./TransactionJournal";
import TransactionsType from "enums/TransactionsType";
import useCustomFieldRenderer from "hooks/useCustomFieldRenderer";
import { FormikTransactionRequirements } from "hooks/useTransactionRequirements";
import FormikCustomFields from "components/common/FormikCustomFields";
import LineItems from "./LineItems";
import TravelDetails, { useTravelIndustries } from "./TravelDetails";
import {
  customFieldParentTypeFor,
  REQUIREMENT_TYPES,
  RequirementsPanel,
  useIntegrationGuess,
} from "./Requirements";
import type { RequirementTransaction } from "./transactionSectionTypes";
import SectionTabs, { type SectionTab } from "components/common/SectionTabs";

interface TransactionSectionsProps {
  transaction: RequirementTransaction;
  formik: FormikProps<FormikTransactionRequirements>;
  refreshLineItems: number;
  setShowIntegrationGuessWarning: (value: boolean) => void;
  refetchTransactionData: () => void;
  handleRefreshTransactions?: () => void;
}

/**
 * The review drawer's sections, as one tab strip under the transaction
 * summary. Which tabs exist depends on the transaction type, the company's
 * entitlements and whether the merchant reported travel data, so the strip is
 * built per transaction rather than declared.
 */
const TransactionSections: React.FC<TransactionSectionsProps> = ({
  transaction,
  formik,
  refreshLineItems,
  setShowIntegrationGuessWarning,
  refetchTransactionData,
  handleRefreshTransactions,
}) => {
  const { t } = useTranslation();

  const { isEntitled: featureLineItemDetails } = useFeatureEntitlements(
    "feature_line_item_details",
  );
  const { isEntitled: featureCustomFields } = useFeatureEntitlements(
    "feature_card_custom_fields",
  );

  const { isEntitled: featureTransactionJournal } = useFeatureEntitlements(
    "feature_transaction_journal",
  );
  const canReadJournal = useCheckUserRole(Role.Auditor);

  const transactionType = transaction?.type;
  const customFieldParentType = customFieldParentTypeFor(transactionType);

  // The panel takes `settings` so its own fallback fetch stays off: the tab
  // only exists when there are fields, which means fetching them out here.
  const { customFieldSettings } = useCustomFieldRenderer({
    parentType:
      featureCustomFields && customFieldParentType
        ? customFieldParentType
        : undefined,
  });

  const { travelData, industries } = useTravelIndustries(
    transaction?.id ?? null,
    transactionType,
  );

  const hasIntegrationGuess = useIntegrationGuess(
    transaction,
    setShowIntegrationGuessWarning,
  );

  const hasRequirements = REQUIREMENT_TYPES.includes(transactionType ?? "");
  const hasLineItems =
    featureLineItemDetails &&
    [TransactionsType.CAPTURE, TransactionsType.AUTHORIZATION].includes(
      transactionType as TransactionsType,
    );
  const hasCustomFields = (customFieldSettings ?? []).length > 0;

  const tabs: SectionTab[] = [];

  if (hasRequirements) {
    tabs.push({
      key: "requirements",
      label: t("Requirements"),
      panel: (
        <RequirementsPanel
          transaction={transaction}
          formik={formik}
          refetchTransactionData={refetchTransactionData}
          handleRefreshTransactions={handleRefreshTransactions}
          hasIntegrationGuess={hasIntegrationGuess}
        />
      ),
    });
  }

  if (hasCustomFields && customFieldParentType) {
    tabs.push({
      key: "customFields",
      label: t("Custom Fields"),
      panel: (
        <FormikCustomFields
          parentType={customFieldParentType}
          settings={customFieldSettings}
          formik={formik}
        />
      ),
    });
  }

  if (hasLineItems) {
    tabs.push({
      key: "lineItems",
      label: t("Line Items"),
      panel: (
        <LineItems
          transactionId={transaction?.id ?? null}
          refresh={refreshLineItems}
        />
      ),
    });
  }

  if (industries.length > 0) {
    tabs.push({
      key: "travel",
      label: t("Travel"),
      panel: <TravelDetails travelData={travelData} industries={industries} />,
    });
  }

  if (
    featureTransactionJournal === true &&
    canReadJournal &&
    transaction.id &&
    transaction.company?.id
  ) {
    tabs.push({
      key: "journal",
      label: t("Journal"),
      panel: (
        <TransactionJournal
          key={`${transaction.company.id}:${transaction.id}`}
          companyId={transaction.company.id}
          transactionId={transaction.id}
        />
      ),
    });
  }

  return (
    <SectionTabs
      tabs={tabs}
      ariaLabel={t("Transaction sections")}
      // An integration guess used to spring the categories accordion open. The
      // category now lives in the requirements panel, so that is this tab.
      focusKey={hasIntegrationGuess ? "requirements" : null}
    />
  );
};

export default TransactionSections;
